import React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import classNames from 'classnames';

import { callbackHooks } from '@nxte-nl/callback-hooks';
import { stateHooks } from '@nxte-nl/state-hooks';
import { StaticImage } from '@nxte-nl/static-image';

import {
  AccountNavigationContainer,
} from '../../containers/AccountNavigationContainer/AccountNavigationContainer';
import {
  ApplicationBuildContainer,
} from '../../containers/ApplicationBuildContainer/ApplicationBuildContainer';
import {
  FeatureToggleWrapContainer,
} from '../../containers/FeatureToggleWrapContainer/FeatureToggleWrapContainer';
import { HeaderMenuContainer } from '../../containers/HeaderMenuContainer/HeaderMenuContainer';
import { SearchContainer } from '../../containers/SearchContainer/SearchContainer';
import {
  StoreSwitcherButtonContainer,
} from '../../containers/StoreSwitcherButtonContainer/StoreSwitcherButtonContainer';
import { hydrationHooks } from '../../hooks/hydrationHooks/hydrationHooks';
import { navigationHooks } from '../../hooks/navigationHooks/navigationHooks';
import { storeSwitcherHooks } from '../../hooks/storeSwitcherHooks/storeSwitcherHooks';
import { versionDependent } from '../../images/versionDependentExport';
import { FeatureToggleValues } from '../../types/FeatureToggles';
import { DropDownMenuPresentational } from '../../types/common';
import { BasketQuantity } from '../BasketQuantity/BasketQuantity';
import {
  DynamicalHeightFullScreenModalWindow,
} from '../DynamicalHeightFullScreenModalWindow/DynamicalHeightFullScreenModalWindow';
import { HeaderDropDown } from '../HeaderDropDown/HeaderDropDown';
import { HeaderMenuContentFooter } from '../HeaderMenuContentFooter/HeaderMenuContentFooter';
import { Link, LinkType } from '../Link/Link';
import { LocalizedLink } from '../LocalizedLink/LocalizedLink';
import { Typography } from '../Typography/Typography';
import { Chevron, Close, Hamburger, Bag } from '../icons';
import { ChevronColor } from '../icons/Chevron/Chevron';
import styles from './HeaderV2.module.css';

export const HEADER_HEIGHT = 80;
export const HEADER_DECORATION_HEIGHT = versionDependent.headerDecoration ? 30 : 0;

const logoSizeDesktop = {
  width: 252,
  height: 86,
};

const logoSizeMobile = {
  width: 116,
  height: 68,
};

const messages = defineMessages({
  giftCardsNavItem: {
    id: 'src.components.Header.giftCardsNavItem',
    defaultMessage: 'Gift cards',
  },
  checkBalanceLink: {
    id: 'src.components.Header.checkBalanceLink',
    defaultMessage: 'Choice Redeem Gift Card',
  },
  customerSupportLink: {
    id: 'src.components.Header.customerSupportLink',
    defaultMessage: 'Customer support',
  },
  choiceGiftcardLink: {
    id: 'src.components.Header.choiceGiftcardLink',
    defaultMessage: 'Choice gift card',
  },
  logoImageAlt: {
    id: 'src.components.Header.logoImageAlt',
    defaultMessage: 'logo',
  },
  basketLink: {
    id: 'src.components.Header.basketLink',
    defaultMessage: 'Basket',
  },
  menuLabelText: {
    id: 'src.components.HeaderV2.menuLabelText',
    defaultMessage: 'Menu',
  },
});

export type Props = {
  basketQuantity?: number;
  businessMenu?: DropDownMenuPresentational | null;
  disableActions?: boolean;
  disableSearch?: boolean;
  isBusinessPageV3: boolean;
  isDesktop: boolean;
  isNavigationIconTextOn?: boolean;
  language: string;
  loading?: boolean;
  orderModalAuthMode?: boolean;
  storeSwitcherHeight: number;
};

export const HeaderV2 = React.memo<Props>(({
  disableActions = false,
  disableSearch = false,
  loading,
  orderModalAuthMode,
  isDesktop,
  storeSwitcherHeight,
  language,
  basketQuantity,
  isNavigationIconTextOn,
  isBusinessPageV3,
  businessMenu,
}) => {
  const intl = useIntl();

  const homePageRedirect =
    navigationHooks.useHomePageRedirect();
  const basketPageRedirect = navigationHooks.useBasketPageRedirect();
  const customerSupportPageRedirect = navigationHooks.useCustomerSupportPageRedirect();
  const redeemRedirect = navigationHooks.useRedeemCheckBalanceRedirect();
  const choiceGiftcardRedirect = navigationHooks.useChoiceGiftcardRoute();
  const {
    value: expanded,
    toggle: toggleMenu,
    setFalse: onCloseMenu,
  } = stateHooks.useBooleanState();
  const onToggleMenu = callbackHooks.useWrapEventHandlers([toggleMenu], true);

  const logoSize = isDesktop ? logoSizeDesktop : logoSizeMobile;
  const offset = HEADER_HEIGHT + HEADER_DECORATION_HEIGHT + storeSwitcherHeight;
  const storeSwitcherButtonLabel = storeSwitcherHooks.useGetStoreSwitcherButtonLabel({
    isOnlyLanguage: true,
  });

  const { isClient } = hydrationHooks.useIsClient();

  return (
    <div className={styles.rootContainer}>
      <header
        className={styles.header}
        color={'var(--header-text-color-v2)'}
      >
        <nav className={classNames(styles.headerContent, {
          [styles.headerContentWithIconText]: isNavigationIconTextOn,
        })}>
          <div className={styles.leftSide}>
            {!isDesktop && !disableActions && (
              <button
                className={classNames(styles.menuDropdownBtn, {
                  [styles.disabled]: !isClient,
                })}
                onClick={onToggleMenu}
                disabled={!isClient}
              >
                <Close
                  className={classNames({
                    [styles.menuDropdownIconVisible]: expanded,
                    [styles.menuDropdownIconInvisible]: !expanded,
                  })}
                  color={'var(--header-text-color-v2)'}
                />

                <Hamburger
                  className={classNames({
                    [styles.menuDropdownIconVisible]: !expanded,
                    [styles.menuDropdownIconInvisible]: expanded,
                  })}
                  color={'var(--header-text-color-v2)'}
                />
                {isNavigationIconTextOn && !expanded
                  && <Typography variant='subText1' className={styles.rightNavLinksText}>
                    <FormattedMessage {...messages.menuLabelText} />
                  </Typography>}
              </button>
            )}

            <Link type={LinkType.Href} to={homePageRedirect()}>
              <StaticImage
                className={styles.logo}
                src={versionDependent.logoV2.src}
                alt={intl.formatMessage(messages.logoImageAlt)}
                width={logoSize.width}
                height={logoSize.height}
              />
            </Link>

            {isDesktop && !disableActions && (
              <>
                <div
                  onClick={onToggleMenu}
                  className={classNames(styles.menuDropdownLinkBtn, {
                    [styles.disabled]: !isClient,
                    [styles.menuDropdownLinkBtnActive]: expanded,
                  })}>
                  <Typography
                    variant='bodySubText1'
                    className={classNames(
                      styles.giftCardDropdownBtnTitle,
                      {
                        [styles.activeNavItem]: expanded,
                      },
                    )}
                  >
                    <FormattedMessage {...messages.giftCardsNavItem} />
                  </Typography>

                  <Chevron
                    variant={expanded ? 'up' : 'down'}
                    fill={
                      expanded
                        ? ChevronColor.secondaryHeaderActive
                        : ChevronColor.secondaryHeader
                    }
                  />
                </div>

                <Link type={LinkType.Href} to={redeemRedirect}>
                  <Typography variant='bodySubText1' className={styles.text}>
                    <FormattedMessage {...messages.checkBalanceLink} />
                  </Typography>
                </Link>
                <ApplicationBuildContainer
                  defaultValue={
                    <Link
                      type={LinkType.Href}
                      to={choiceGiftcardRedirect}
                      className={styles.choiceGiftcardLink}
                    >
                      <Typography variant='bodySubText1' className={styles.text}>
                        <FormattedMessage {...messages.choiceGiftcardLink} />
                      </Typography>
                    </Link>
                  }
                  CK={null}
                />
              </>
            )}
          </div>

          {!disableActions && (
            <div className={styles.rightSide}>
              {isDesktop && (
                <div className={styles.rightNavLinks}>
                  { businessMenu?.title && (businessMenu.menuItems.length !== 0)
                  && <HeaderDropDown
                    title={businessMenu.title}
                    dropDownItems={businessMenu.menuItems}
                    className={styles.businessPageLink}
                  />}
                  <LocalizedLink to={customerSupportPageRedirect()} isTargetBlank>
                    <Typography variant='bodySubText1' className={styles.rightNavLinksText}>
                      <FormattedMessage {...messages.customerSupportLink} />
                    </Typography>
                  </LocalizedLink>
                </div>
              )}

              <div className={styles.rightNavIcons}>
                {orderModalAuthMode
                && <AccountNavigationContainer pageLoading={loading} isHeaderV2 />}

                <Link
                  type={LinkType.Href}
                  to={basketPageRedirect}
                  className={styles.basketLink}
                  withoutHoverStyles
                >
                  <>
                    <Bag
                      fill={'var(--header-text-color-v2)'}
                    />
                    <Typography
                      variant='subText1'
                      className={styles.rightNavLinksText}>
                      <FormattedMessage {...messages.basketLink} />
                    </Typography>
                    { basketQuantity
                      && <BasketQuantity
                        quantity={basketQuantity}
                        isV2
                      />}
                  </>
                </Link>
                {isDesktop && (
                  <FeatureToggleWrapContainer>
                    {({ languageSwitcher }: FeatureToggleValues) => (
                      <>
                        {languageSwitcher ? (
                          <StoreSwitcherButtonContainer
                            label={storeSwitcherButtonLabel}
                            isNonBlockPart
                            isHeaderPart
                            textClassName={styles.textClassName}
                            iconFill={'var(--header-text-color-v2)'}
                          />
                        ) : null}
                      </>
                    )}
                  </FeatureToggleWrapContainer>
                )}
              </div>
            </div>
          )}
        </nav>
      </header>

      {!expanded && !disableSearch && (
        <SearchContainer
          storeSwitcherHeight={storeSwitcherHeight}
          headerHeight={HEADER_HEIGHT + HEADER_DECORATION_HEIGHT}
        />
      )}

      {!disableActions
        && <DynamicalHeightFullScreenModalWindow
          isExpanded={expanded}
          offset={offset}
          onClose={onCloseMenu}
          shouldCloseOnEsc
          headerHeight={HEADER_HEIGHT + HEADER_DECORATION_HEIGHT}
          isBlockOutsideClick={!isDesktop}
        >
          <div className={styles.headerMenuWrapper}>
            <HeaderMenuContainer />

            {!isDesktop
            && <HeaderMenuContentFooter
              businessMenu={businessMenu}
              language={language}
              isBusinessPageV3={isBusinessPageV3}
            />}
          </div>
        </DynamicalHeightFullScreenModalWindow>}
    </div >
  );
});

HeaderV2.displayName = 'HeaderV2';
